import { nextTick, onBeforeUnmount, watch, type Ref } from 'vue';
import { message } from 'ant-design-vue';
import { useI18n } from 'vue-i18n';
import {
  clearCorrectedJwnetError,
  clearJwnetErrors,
  scrollToFirstFormError,
  validateJwnetInputs
} from './modules/jwnet';

interface FormInstance {
  $el: HTMLElement;
  validate?: () => Promise<unknown>;
}

export function useJwnetValidation(formRef: Ref<FormInstance | undefined>) {
  const { t } = useI18n();
  const root = () => formRef.value?.$el;
  let listeningRoot: HTMLElement | undefined;

  watch(
    formRef,
    () => {
      listeningRoot?.removeEventListener('input', clearCorrectedJwnetError);
      listeningRoot = root();
      listeningRoot?.addEventListener('input', clearCorrectedJwnetError);
    },
    { flush: 'post' }
  );

  const clearErrors = () => {
    const element = root();
    if (element) clearJwnetErrors(element);
  };

  // Also used by Ant forms' finish/finishFailed events and the correction
  // dialog's existing manual validation. Both error types are collected.
  const finishValidation = async (existingValid: boolean): Promise<boolean> => {
    await nextTick();
    const element = root();
    if (!element) return false;
    const invalid = validateJwnetInputs(element);
    if (invalid.length) {
      message.error({
        key: 'jwnet-character-error',
        content: t('jwnet_forbidden_characters')
      });
    }
    if (!existingValid || invalid.length) {
      scrollToFirstFormError(element);
      return false;
    }
    return true;
  };

  const validateBeforeSubmit = async (): Promise<boolean> => {
    let existingValid = true;
    try {
      await formRef.value?.validate?.();
    } catch {
      existingValid = false;
    }
    return finishValidation(existingValid);
  };

  onBeforeUnmount(() => {
    listeningRoot?.removeEventListener('input', clearCorrectedJwnetError);
    clearErrors();
  });

  return { validateBeforeSubmit, finishValidation, clearErrors };
}
